import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGroupStore } from '../store/groupStore';
import { GroupSettings } from '../components/group/GroupSettings';
import  TeamPage  from './TeamPage'
import { DashboardLayout } from '../layouts/DashboardLayout';
import { WorkforceBillingSection } from '../components/billing/WorkforceBillingSection';
import { 
  Users, 
  Settings, 
  Bell, 
  Shield, 
  CreditCard, 
  UserCheck,
  Key,
  MapPin
} from 'lucide-react';
import { LocationsSettings } from '../components/group/settings/LocationsSettings';

interface SettingsTab {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<any>;
}



const NotificationsSettings: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Email Notifications</h4>
          <p className="text-sm text-gray-500">Receive updates via email</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" defaultChecked />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Push Notifications</h4>
          <p className="text-sm text-gray-500">Receive push notifications</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
    </div>
  </div>
);

const SecuritySettings: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium text-gray-900 mb-4">Security & Privacy</h3>
    <div className="space-y-4">
      <div className="p-4 border rounded-lg">
        <div className="flex items-center space-x-3 mb-2">
          <Key className="w-5 h-5 text-gray-600" />
          <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
        </div>
        <p className="text-sm text-gray-500 mb-3">Add an extra layer of security to your account</p>
        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
          Enable 2FA
        </button>
      </div>
      
      <div className="p-4 border rounded-lg">
        <div className="flex items-center space-x-3 mb-2">
          <Shield className="w-5 h-5 text-gray-600" />
          <h4 className="font-medium text-gray-900">Session Management</h4>
        </div>
        <p className="text-sm text-gray-500 mb-3">Manage active sessions and devices</p>
        <button className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
          Sign Out All Devices
        </button>
      </div>
    </div>
  </div>
);

export const GroupSettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const group = useGroupStore((state) =>
    state.groups.find((g) => g.id === 'group-1')
  );

  const [activeTab, setActiveTab] = useState<string>('general');

  const settingsTabs: SettingsTab[] = [
    {
      id: 'general',
      label: 'General',
      icon: Settings,
      component: GroupSettings
    },
    {
      id: 'locations',
      label: 'Locations',
      icon: MapPin,
      component: LocationsSettings
    },
    {
      id: 'teams',
      label: 'Teams',
      icon: Users,
      component: TeamPage
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      component: NotificationsSettings
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      component: SecuritySettings
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard,
      component: WorkforceBillingSection
    },
  ];

  const ActiveComponent = settingsTabs.find(tab => tab.id === activeTab)?.component || GroupSettings;

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Practice Settings</h1>
              <p className="mt-2 text-gray-600">
                Manage your practice's settings and configurations
              </p>
            </div>

            {/* Settings Navigation */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Settings">
                  {settingsTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Settings Content */}
              <div className="p-6">
                <ActiveComponent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};